import React, { useCallback, useState } from "react"
import { v4 as uuidv4 } from "uuid"

import FileUploadLabel from "./FileUploadLabel"
import { Button, Container, Grid, Stack, useTheme } from "@mui/material"
import { useTranslation } from "react-i18next"

import { TypedValidationError, UploadedJobFile } from "./expiringFiles"
import { useGlobalLoader, useLogger } from "src/providers"
import FileDropzoneItem from "./FileDropzoneItem"
import { scrollbarMixin } from "src/providers/AppProviders"
import { useResponsive } from "src/utils/useResponsive"

export const acceptableFileFormats = [
  ".docx",
  ".doc",
  ".json",
  ".xlsx",
  ".xls",
  ".xlf",
  ".odt",
  ".odp",
  ".mqxliff",
  ".pptx",
  ".ppt",
  ".pdf",
  ".html",
  ".idml",
  ".properties",
  ".xml",
  ".srt",
  ".vtt",
  ".tmx"
]

export const acceptableFileTypes = [
  "application/pdf",
  "application/json",
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/html",
  "text/xml",
  "text/vtt",
  "text/tmx",
  ".doc",
  ".xls",
  ".ppt",
  ".json",
  ".xlsx",
  ".xlf",
  ".odt",
  ".mqxliff",
  ".pptx",
  ".html",
  ".idml",
  ".properties",
  ".xml",
  ".srt", 
  ".vtt",
  ".tmx"
]

export const acceptableZipFileTypes = [
  ".zip",
  "application/zip",
  "application/x-zip",
  "application/octet-stream",
  "application/x-zip-compressed"
]

export function formatFileSize(size?: number): string {
  if (size === undefined) return ""
  if (size < 1024) {
    return `${size} B`
  } else if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`
  }
  return `${(size / 1024 / 1024).toFixed(2)} MB`
}

export function isZipFile({ type }: File): boolean {
  return acceptableZipFileTypes.includes(type)
}

export function isFileTypeSupported({ type, name }: File, acceptableFileTypes: string[]): boolean {
  const extension = "." + name.split(".").pop()?.toLocaleLowerCase()
  return type ? acceptableFileTypes.includes(type) : acceptableFileTypes.includes(extension)
}

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4.74697 4.74717C4.94513 4.54901 5.2139 4.43768 5.49414 4.43768C5.77438 4.43768 6.04315 4.54901 6.24131 4.74717L19.6903 18.1962C19.8885 18.3943 19.9998 18.6631 19.9998 18.9433C19.9998 19.2236 19.8885 19.4924 19.6903 19.6905C19.4922 19.8887 19.2234 20 18.9432 20C18.6629 20 18.3941 19.8887 18.196 19.6905L4.74697 6.2415C4.54881 6.04334 4.43749 5.77458 4.43749 5.49433C4.43749 5.21409 4.54881 4.94533 4.74697 4.74717Z"
      fill="#42526E"
    />
    <path
      d="M4.74717 18.1962C4.54901 18.3944 4.43768 18.6631 4.43768 18.9434C4.43768 19.2236 4.54901 19.4924 4.74717 19.6905C4.94533 19.8887 5.21409 20 5.49434 20C5.77458 20 6.04334 19.8887 6.2415 19.6905L19.6905 6.24151C19.8887 6.04335 20 5.77459 20 5.49435C20 5.21411 19.8887 4.94534 19.6905 4.74718C19.4924 4.54902 19.2236 4.43769 18.9433 4.43769C18.6631 4.43769 18.3943 4.54902 18.1962 4.74718L4.74717 18.1962Z"
      fill="#42526E"
    />
  </svg>
)

export const MAX_FILE_SIZE = 100 * 1024 * 1024

export interface ToUploadFile {
  file: File
  id: string
  size: number
  name: string
}

export interface FilesItem {
  uploadedFile?: UploadedJobFile
  toUploadFile?: ToUploadFile
}

export interface MaxSizeConfig {
  default: number
  byExtension?: Record<string, number>
  byMimeType?: Record<string, number>
}

interface FileUploadProps {
  id: string
  files: FilesItem[]
  setFiles: React.Dispatch<React.SetStateAction<FilesItem[]>>
  fileFormats: string[]
  fileFormatsDescription?: string
  maxSizeDescription?: string
  maxSize?: number | MaxSizeConfig
  fileTypes: string[]
  placing: string //where file upload is used - only for activity logging
  languageFrom?: string
  showRemoveAll?: boolean
  uploadFiles: (file: File) => Promise<UploadedJobFile | UploadedJobFile[]>
  deleteFile?: (fileId: string) => Promise<void>
}

const consumeEvent = (e: any) => {
  e.preventDefault()
  e.stopPropagation()
}

// Helper function to get max size for a specific file
function getMaxSizeForFile(file: File, maxSizeConfig?: number | MaxSizeConfig): number {
  // If maxSize is a number or undefined, return it (backward compatibility)
  if (typeof maxSizeConfig === "number" || maxSizeConfig === undefined) {
    return maxSizeConfig || MAX_FILE_SIZE
  }

  // Check by MIME type first
  if (maxSizeConfig.byMimeType && file.type && maxSizeConfig.byMimeType[file.type]) {
    return maxSizeConfig.byMimeType[file.type]
  }

  // Check by extension
  if (maxSizeConfig.byExtension) {
    const extension = "." + file.name.split(".").pop()?.toLowerCase()
    if (extension && maxSizeConfig.byExtension[extension]) {
      return maxSizeConfig.byExtension[extension]
    }
  }

  // Return default from config
  return maxSizeConfig.default
}

interface FileDropzoneAreaProps extends FileUploadProps {
  isDragOver: boolean
  setDragOver: React.Dispatch<React.SetStateAction<boolean>>
}

// Inner component: Handles drag/drop functionality
export const FileDropzoneArea = ({ files, setFiles, isDragOver, setDragOver, ...props }: FileDropzoneAreaProps) => {
  const { showLoader, hideLoader } = useGlobalLoader()
  const { logger } = useLogger()
  const { t } = useTranslation("filesUpload")
  const { isMobile } = useResponsive()
  const processedFileIds = React.useRef<Set<string>>(new Set())

  // Auto-upload files after they're added to state
  React.useEffect(() => {
    files.forEach((fileItem) => {
      const { toUploadFile, uploadedFile } = fileItem
      // Only process files that have toUploadFile and haven't been uploaded/processed yet
      if (toUploadFile && !uploadedFile && !processedFileIds.current.has(toUploadFile.id)) {
        processedFileIds.current.add(toUploadFile.id)
        uploadFile(toUploadFile)
      }
    })
  }, [files])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target
    if (!files) return
    Array.from(files).forEach((file) => {
      readFile(file)
    })
  }

  const handleRemoveFile = useCallback(
    (toUploadFileId: string) => {
      setFiles((prevState) => prevState.filter(({ toUploadFile }) => toUploadFile?.id !== toUploadFileId))
      processedFileIds.current.delete(toUploadFileId)
      logger.info(`User - removed file in ${props.placing} file upload: ${toUploadFileId}`)
    },
    [files, setFiles]
  )

  const handleRemoveUploadedFile = useCallback(
    (fileId: string) => {
      props.deleteFile &&
        props.deleteFile(fileId).catch((error) => {
          logger.error(`User - error when removing file ${fileId} in ${props.placing} file upload`, error)
        })

      setFiles((prevState) => prevState.filter(({ uploadedFile }) => uploadedFile?.id !== fileId))
      logger.info(`User - removed file in ${props.placing} file upload: ${fileId}`)
    },
    [files, setFiles]
  )

  const handleFileUpload = useCallback(
    (fileId: string, response: UploadedJobFile | UploadedJobFile[]) => {
      setFiles((prevState) => {
        const arr: FilesItem[] = []
        prevState.forEach(({ toUploadFile, uploadedFile }) => {
          if ((uploadedFile ? uploadedFile.id : toUploadFile?.id) !== fileId) {
            arr.push({ toUploadFile, uploadedFile })
            return
          }
          if (Array.isArray(response)) {
            arr.push(...(response as UploadedJobFile[]).map((file) => ({ uploadedFile: file })))
            return
          }
          arr.push({ toUploadFile, uploadedFile: response as UploadedJobFile })
        })
        const uploadedFilesBefore = prevState
          .filter(({ uploadedFile }) => uploadedFile !== undefined)
          .map((item) => item.uploadedFile?.id as string)
        const uploadedFilesAfter = arr
          .filter(({ uploadedFile }) => uploadedFile !== undefined)
          .map((item) => item.uploadedFile?.id as string)
        logger.info(
          `User - uploaded new file in ${props.placing} file upload, uploaded files before ${uploadedFilesBefore}, after ${uploadedFilesAfter}`
        )
        return [...new Map(arr.map((item) => [item.uploadedFile?.id || item.toUploadFile?.id, item])).values()]
      })
    },
    [files, setFiles]
  )

  const uploadFile = (toUploadFile: ToUploadFile) => {
    const { file, id } = toUploadFile
    const { name, size } = file
    const maxSize = getMaxSizeForFile(file, props.maxSize)
    
    if (file.size > maxSize) {
      handleFileUpload(id, {
        id,
        name,
        size,
        typedValidationError: {
          type: "WRONG_FILE_FORMAT",
          description: "File size is too large",
          message: "File size is too large"
        }
      })
    } else {
      showLoader()
      props
        .uploadFiles(file)
        .then((response) => handleFileUpload(id, response))
        .catch((error) => {
          if (error?.response as TypedValidationError) {
            handleFileUpload(id, { id, name, size, typedValidationError: error.response })
          }
        })
        .finally(() => {
          hideLoader()
        })
    }
  }

  const readFile = (file: File) => {
    setFiles((prevState) => {
      // Check for duplicates
      if (prevState.filter((f) => f.uploadedFile?.name == file.name).length > 0) {
        return prevState
      }

      const toUploadFile = {
        file,
        id: uuidv4(),
        size: file.size,
        name: file.name
      }

      // Just add to state - useEffect will handle upload
      return [...prevState, { toUploadFile }]
    })
  }

  const traverseFileTree = (item: any, path = "") => {
    if (item.isFile) {
      // Get file
      item.file(function (file: File) {
        if (!isFileTypeSupported(file, props.fileTypes)) return
        readFile(file)
      })
    } else if (item.isDirectory) {
      // Get folder contents
      const dirReader = item.createReader()
      dirReader.readEntries(function (entries: any) {
        for (let i = 0; i < entries.length; i++) {
          traverseFileTree(entries[i], path + item.name + "/")
        }
      })
    }
  }

  const onDrop = (e: React.DragEvent<HTMLElement>) => {
    consumeEvent(e)
    const { items } = e.dataTransfer
    for (let i = 0; i < items.length; i++) {
      // webkitGetAsEntry is where the magic happens
      const item = items[i].webkitGetAsEntry()
      if (item) {
        traverseFileTree(item)
      }
    }

    setDragOver(false)
  }

  const onDragEnter = (e: React.DragEvent) => {
    consumeEvent(e)
  }

  const onDragOver = (e: React.DragEvent<HTMLElement>) => {
    consumeEvent(e)
    setDragOver(true)
  }

  const onDragLeave = (e: React.DragEvent) => {
    consumeEvent(e)
    setDragOver(false)
  }

  const removeAllFiles = (e: React.MouseEvent) => {
    consumeEvent(e)
    setFiles([])
    processedFileIds.current.clear()
    logger.info(`${props.placing} file upload - remove all files clicked`)
  }

  const openFileBrowser = () => {
    document.getElementById(props.id)?.click()
  }

  return (
    <>
      <input type="file" onChange={handleChange} id={props.id} multiple hidden accept={props.fileTypes.join(", ")} />
      <label
        htmlFor={props.id}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        style={{
          height: "100%",
          borderRadius: "inherit",
          cursor: "pointer",
          display: "block"
        }}
      >
        <Stack justifyContent={isMobile ? "center" : "space-evenly"} alignItems={"center"} sx={{ height: "100%" }}>
          {!files.length && (
            <FileUploadLabel
              maxFileSize={
                props.maxSizeDescription ||
                formatFileSize(
                  typeof props.maxSize === "number" || props.maxSize === undefined
                    ? props.maxSize || MAX_FILE_SIZE
                    : props.maxSize.default
                )
              }
              fileFormats={props.fileFormatsDescription || props.fileFormats.join(", ")}
              isDragOver={isDragOver}
            />
          )}
          {!!files.length && (
            <Grid
              container
              spacing={0.5}
              justifyContent="center"
              className="file-upload"
              sx={{
                p: 1,
                px: { xs: 2, md: 10 },
                maxHeight: "min(250px, calc(100% - 80px))",
                zIndex: 1000,
                overflowY: "auto",
                ...scrollbarMixin("medium")
              }}
            >
              {files
                .filter((file): file is FilesItem & { toUploadFile: ToUploadFile } => file.toUploadFile !== undefined)
                .filter((file) => file.uploadedFile === undefined)
                .map((file) => (
                  <Grid item xs={12} key={file.toUploadFile.id}>
                    <FileDropzoneItem
                      fileDetails={file.toUploadFile}
                      onFileRemove={handleRemoveFile}
                      expectedLanguage={props.languageFrom}
                    />
                  </Grid>
                ))}
              {files
                .filter(
                  (file): file is FilesItem & { uploadedFile: UploadedJobFile } => file.uploadedFile !== undefined
                )
                .map((file) => (
                  <Grid item xs={12} key={file.uploadedFile.id}>
                    <FileDropzoneItem
                      fileDetails={file.uploadedFile}
                      onFileRemove={handleRemoveUploadedFile}
                      expectedLanguage={props.languageFrom}
                    />
                  </Grid>
                ))}
            </Grid>
          )}
          <Stack>
            {props.showRemoveAll && !!files.length && (
              <Button
                onClick={removeAllFiles}
                sx={{
                  "&:hover": {
                    backgroundColor: "transparent",
                    border: "none",
                    boxShadow: "none",
                    fontWeight: 600
                  }
                }}
              >
                <CloseIcon />
                {t("removeAllFiles")}
              </Button>
            )}
            <Button variant="contained" color="secondary" onClick={openFileBrowser} data-testid="upload-files-button">
              {t("uploadFiles")}
            </Button>
          </Stack>
        </Stack>
      </label>
    </>
  )
}

// Outer component: Handles styling
export const FileDropzoneContainer = ({ children, isDragOver }: { children: React.ReactNode; isDragOver: boolean }) => {
  const { palette } = useTheme()
  const { isMobile } = useResponsive()

  return (
    <Container
      sx={{
        background: "#FAFAFA 0% 0% no-repeat padding-box",
        border: "1px dashed #BABABA",
        borderRadius: "25px",
        height: { xs: "auto", md: "100%" },
        ...(isMobile && {
          flex: 1
        }),
        "&.MuiContainer-root": {
          pl: { xs: 2, md: 10 },
          pr: { xs: 2, md: 10 }
        },
        ...(isDragOver ? { border: `1px dashed ${palette.primary.main}`, backgroundColor: palette.primary.light } : {})
      }}
    >
      {children}
    </Container>
  )
}

const FileDropzone = ({ files, setFiles, ...props }: FileUploadProps) => {
  const [isDragOver, setDragOver] = useState<boolean>(false)

  return (
    <FileDropzoneContainer isDragOver={isDragOver}>
      <FileDropzoneArea files={files} setFiles={setFiles} isDragOver={isDragOver} setDragOver={setDragOver} {...props} />
    </FileDropzoneContainer>
  )
}

export default FileDropzone
